/**
 * Where to scroll so a card sits where the admin is looking: centred when it
 * fits in the viewport (with `margin` to spare), otherwise with its bottom
 * edge, where the new content is, at the bottom of the viewport. Clamped to
 * the page, so a card at the very top or bottom asks for a reachable spot.
 */
export function focusTop(rect, { viewportHeight, scrollY, pageHeight, margin }) {
    const top = rect.height <= viewportHeight - margin
        ? scrollY + rect.top - (viewportHeight - rect.height) / 2
        : scrollY + rect.bottom - viewportHeight;

    return Math.round(Math.min(Math.max(top, 0), Math.max(pageHeight - viewportHeight, 0)));
}

/**
 * Brings an Integrationer card into focus after a user action changed what
 * it shows (opening the panel, a form appearing). Only ever called from a
 * click: renders, morphs and a panel starting again never move the page.
 *
 * Waits for the change to paint, then scrolls smoothly; where smooth
 * scrolling does nothing, jumps there after `fallbackDelay` instead.
 * Resolves true when it scrolled, false when the card was already in place.
 */
export function focusCard(el, { margin = 32, fallbackDelay = 400 } = {}) {
    return new Promise((resolve) => {
        window.Alpine.nextTick(() => requestAnimationFrame(() => {
            const top = focusTop(el.getBoundingClientRect(), {
                viewportHeight: window.innerHeight,
                scrollY: window.scrollY,
                pageHeight: document.documentElement.scrollHeight,
                margin,
            });
            const before = window.scrollY;

            if (Math.abs(top - before) <= 2) {
                resolve(false);
                return;
            }

            if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
                window.scrollTo({ top, behavior: 'instant' });
                resolve(true);
                return;
            }

            window.scrollTo({ top, behavior: 'smooth' });
            setTimeout(() => {
                if (Math.abs(window.scrollY - before) < 2) {
                    window.scrollTo({ top, behavior: 'instant' });
                }
                resolve(true);
            }, fallbackDelay);
        }));
    });
}

document.addEventListener('alpine:init', () => {
    window.Alpine.magic('focusCard', () => focusCard);
});
